<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NButton, NEmpty, NModal } from 'naive-ui'
import { useWorkspaceStore } from '@/store/workspace'
import { resolveResourceUrlFromFileName } from '@/features/virtual-resource/fileNamePath'

type ImageOptionPickerItem = {
  index: number
  label: string
  name: string
  noneImagePath: string
  moveImagePath: string
}

const props = defineProps<{
  show: boolean
  title?: string
  selectedIndex: number
  items: ImageOptionPickerItem[]
  graphicBaseFolderPath?: string
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  select: [value: number]
}>()

const workspace = useWorkspaceStore()
const hoverPreview = ref(false)
const currentIndex = ref(-1)
const listRef = ref<HTMLElement | null>(null)

const modalTitle = computed(() => props.title?.trim() || '选择图片选项')
const modalWidth = 'min(920px, 94vw)'
const layoutHeight = '520px'
const selectedItem = computed(() => props.items.find((item) => item.index === currentIndex.value) ?? null)
const selectedNoneUrl = computed(() =>
  resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    selectedItem.value?.noneImagePath ?? '',
    workspace.resourcePreviewOverrides,
    props.graphicBaseFolderPath,
  ),
)
const selectedMoveUrl = computed(() =>
  resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    selectedItem.value?.moveImagePath ?? '',
    workspace.resourcePreviewOverrides,
    props.graphicBaseFolderPath,
  ),
)
const testPreviewUrl = computed(() => {
  if (hoverPreview.value && selectedMoveUrl.value) return selectedMoveUrl.value
  return selectedNoneUrl.value || selectedMoveUrl.value
})
const showButtonTest = computed(() => Boolean(selectedNoneUrl.value || selectedMoveUrl.value))

function closeModal() {
  emit('update:show', false)
}

function confirmSelect() {
  if (!selectedItem.value) return
  emit('select', selectedItem.value.index)
  closeModal()
}

function handleItemClick(item: ImageOptionPickerItem, event: MouseEvent) {
  event.preventDefault()
  currentIndex.value = item.index
}

function handleItemDoubleClick(item: ImageOptionPickerItem, event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  currentIndex.value = item.index
  emit('select', item.index)
  closeModal()
}

async function scrollCurrentItemIntoView() {
  await nextTick()
  const target = listRef.value?.querySelector<HTMLElement>(
    `[data-image-option-index="${CSS.escape(String(currentIndex.value))}"]`,
  )
  target?.scrollIntoView({ block: 'nearest' })
}

watch(
  () => props.show,
  (show) => {
    if (!show) return
    hoverPreview.value = false
    currentIndex.value = props.items.some((item) => item.index === props.selectedIndex)
      ? props.selectedIndex
      : (props.items[0]?.index ?? -1)
    void scrollCurrentItemIntoView()
  },
  { immediate: true },
)
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    class="image-option-picker-modal"
    :title="modalTitle"
    :auto-focus="false"
    :style="{ width: modalWidth }"
    @update:show="emit('update:show', $event)"
  >
    <div class="image-option-picker-shell">
      <div class="image-option-picker-layout" :style="{ height: layoutHeight, maxHeight: '70vh' }">
        <section class="image-option-picker-panel image-option-picker-panel--list">
          <div class="image-option-picker-caption">选项列表</div>
          <div v-if="items.length" ref="listRef" class="picker-scroll-list picker-scroll-list--padded">
            <button
              v-for="item in items"
              :key="`picker-image-option-${item.index}`"
              :data-image-option-index="item.index"
              type="button"
              class="picker-scroll-item"
              :class="{ 'picker-scroll-item--active': item.index === currentIndex }"
              @click="handleItemClick(item, $event)"
              @dblclick="handleItemDoubleClick(item, $event)"
            >
              {{ item.label }}
            </button>
          </div>
          <n-empty v-else description="当前没有图片选项" size="small" />
        </section>

        <section class="image-option-picker-panel image-option-picker-panel--preview">
          <div class="image-option-picker-caption">选项预览</div>
          <div v-if="selectedItem" class="image-option-preview">
            <div class="image-option-preview-title">{{ selectedItem.label }}</div>
            <div class="image-option-preview-name">{{ selectedItem.name || '未命名按钮' }}</div>

            <div class="image-option-preview-grid">
              <div class="image-option-preview-block">
                <div class="image-option-preview-label">未点击图片</div>
                <div class="image-option-preview-box">
                  <img v-if="selectedNoneUrl" :src="selectedNoneUrl" alt="未点击图片预览" draggable="false" />
                  <span v-else>暂无图片</span>
                </div>
              </div>

              <div class="image-option-preview-block">
                <div class="image-option-preview-label">点击图片</div>
                <div class="image-option-preview-box">
                  <img v-if="selectedMoveUrl" :src="selectedMoveUrl" alt="点击图片预览" draggable="false" />
                  <span v-else>暂无图片</span>
                </div>
              </div>
            </div>

            <template v-if="showButtonTest">
              <div class="image-option-preview-label">按钮测试</div>
              <div class="image-option-test-shell">
                <button
                  type="button"
                  class="image-option-test-button"
                  @mouseenter="hoverPreview = true"
                  @mouseleave="hoverPreview = false"
                >
                  <img :src="testPreviewUrl" alt="按钮测试预览" draggable="false" />
                </button>
              </div>
            </template>
          </div>
          <n-empty v-else description="请选择图片选项" size="small" />
        </section>
      </div>

      <div class="image-option-picker-actions">
        <n-button size="small" @click="closeModal">取消</n-button>
        <n-button size="small" type="primary" :disabled="!selectedItem" @click="confirmSelect">确定</n-button>
      </div>
    </div>
  </n-modal>
</template>

<style scoped>
:global(.image-option-picker-modal.n-modal) {
  max-width: 96vw;
}

.image-option-picker-shell {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 8px;
  height: min(600px, calc(100vh - 160px));
  min-height: 0;
}

.image-option-picker-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 8px;
  min-height: 0;
}

.image-option-picker-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.image-option-picker-caption {
  padding: 6px 8px;
  border-bottom: 1px solid var(--theme-border);
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.image-option-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  padding: 8px;
  overflow: auto;
}

.image-option-preview-title {
  font-size: 12px;
  color: var(--theme-text-heading);
}

.image-option-preview-name {
  font-size: 11px;
  color: var(--theme-text-secondary);
}

.image-option-preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.image-option-preview-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.image-option-preview-label {
  font-size: 11px;
  color: var(--theme-text-secondary);
}

.image-option-preview-box,
.image-option-test-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
  overflow: hidden;
}

.image-option-preview-box img,
.image-option-test-button img {
  display: block;
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
}

.image-option-preview-box span {
  font-size: 11px;
  color: var(--theme-text-secondary);
}

.image-option-test-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  min-height: 40px;
  padding: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-heading);
}

.image-option-picker-actions {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px solid var(--theme-border);
}

@media (max-width: 860px) and (orientation: portrait) {
  :global(.image-option-picker-modal.n-modal) {
    width: calc(100vw - 16px) !important;
  }

  :global(.image-option-picker-modal.n-modal .n-card__content) {
    min-height: 0;
  }

  .image-option-picker-shell {
    height: min(620px, calc(100vh - 128px));
    height: min(620px, calc(100dvh - 128px));
  }

  .image-option-picker-layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(110px, 32%) minmax(0, 1fr);
    height: auto !important;
    max-height: none !important;
  }

  .image-option-preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
